import React, { useEffect, useState } from 'react';
import { getApiKeys, deleteApiKey, assignRole, unassignRole, generateApiKey, getAllClientRoles } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import copy from 'copy-to-clipboard';
import { Modal, Button, Spinner, Offcanvas, Form, Alert } from 'react-bootstrap';
import '../styles/ApiKeyList.css';
import { roleHierarchy } from '../services/config'

const ApiKeyList = () => {
    const [apiKeys, setApiKeys] = useState([]);
    const [message, setMessage] = useState({});
    const [visibility, setVisibility] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedApiKey, setSelectedApiKey] = useState(null);
    const [showRoleSidebar, setShowRoleSidebar] = useState(false);
    const [showCreateUserSidebar, setShowCreateUserSidebar] = useState(false);
    const [availableRolesFromApi, setAvailableRolesFromApi] = useState([]);
    const [createFormData, setCreateFormData] = useState({ username: '', email: '', expiry: '' });
    const [creatingUser, setCreatingUser] = useState(false);


    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('adminToken');
            try {
                // Fetch all available roles from API
                const rolesResponse = await getAllClientRoles(token);
                if (rolesResponse.data.success) {
                    const roleNames = rolesResponse.data.data.map(role => role.name);
                    setAvailableRolesFromApi([roleNames.pop("SuperAdmin")]);
                }

                // Fetch API keys for this user
                const response = await getApiKeys(token);
                setApiKeys(response.data.data);
            } catch (error) {
                setMessage({
                    type: 'error',
                    text: 'Error fetching data: ' + (error.response?.status || error.message),
                });
            }
        };

        fetchData();
    }, []);

    const handleDelete = async (apiKeyId) => {
        const token = localStorage.getItem('adminToken');
        try {
            await deleteApiKey(token, apiKeyId);
            setMessage({
                type: 'success',
                text: 'API key deleted successfully!',
            });
            setApiKeys(apiKeys.filter((apiKey) => apiKey.id !== apiKeyId));
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Error deleting API key: ' + error,
            });
        }
    };

    const toggleVisibility = (id) => {
        setVisibility((prevState) => ({
            ...prevState,
            [id]: !prevState[id],
        }));
    };

    const handleCopy = (apiKey) => {
        copy(apiKey);
        setMessage({
            type: 'info',
            text: 'API key copied to clipboard!',
        });
    };

    const handleRoleChange = async (action, role) => {
        setLoading(true);
        const token = localStorage.getItem('adminToken');
        const username = selectedApiKey?.username;

        try {
            let res;
            if (action === 'assign') {
                res = await assignRole(token, username, [role]);
            } else {
                res = await unassignRole(token, username, [role]);
            }
            console.log(res)
            if (res.status ==200 && res.data["success"]){
                setMessage({
                    type: 'success',
                    text: `Role ${action === 'assign' ? 'assigned' : 'unassigned'} successfully!`,
                });
            }else{
                setMessage({
                    type: 'fail',
                    text: `Role ${action === 'assign' ? 'assigned' : 'unassigned'} un-successfull!`,
                });
            }

            // Re-fetch API keys to reflect the changes
            const response = await getApiKeys(token);
            setApiKeys(response.data.data);
        } catch (error) {
            setMessage({
                type: 'error',
                text: `Error updating role: ${error}`,
            });
        } finally {
            setLoading(false);
            setShowRoleSidebar(false); // Close sidebar after action
        }
    };

    const handleOpenRoleSidebar = (apiKey) => {
        setSelectedApiKey(apiKey);
        // Get assigned roles from JWT token (source of truth)
        const token = localStorage.getItem('adminToken');
        try {
            const decodedToken = jwtDecode(token);
            const resourceAccess = decodedToken.resource_access || {};
            // Assuming ml_studio is the client ID
            const clientId = "ml_studio"
            
            if (clientId) {
                const userRoles = resourceAccess[clientId]?.roles || [];
                // Update the selected API key with JWT roles
                setSelectedApiKey({
                    ...apiKey,
                    user_role: userRoles
                });
            }
        } catch (error) {
            console.error('Error decoding JWT:', error);
        }
        setShowRoleSidebar(true);
    };

    const handleCreateUserClick = () => {
        setShowCreateUserSidebar(true);
    };

    const handleCreateUserSubmit = async (e) => {
        e.preventDefault();
        if (!createFormData.username || !createFormData.email || !createFormData.expiry) {
            setMessage({ type: 'error', text: 'Please fill all fields' });
            return;
        }

        setCreatingUser(true);
        const token = localStorage.getItem('adminToken');
        try {
            await generateApiKey(
                token,
                createFormData.username,
                createFormData.email,
                createFormData.expiry,
                []
            );
            setMessage({ type: 'success', text: 'User created successfully!' });
            setCreateFormData({ username: '', email: '', expiry: '' });
            setShowCreateUserSidebar(false);

            // Re-fetch API keys
            const response = await getApiKeys(token);
            setApiKeys(response.data.data);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error creating user: ' + error.message });
        } finally {
            setCreatingUser(false);
        }
    };

    // Split the available roles into assigned and unassigned based on selected user roles
    const splitRoles = () => {
        const assignedRoles = selectedApiKey?.user_role || [];
        const assignableRoles = availableRolesFromApi.filter(role => !assignedRoles.includes(role));
        
        return { assignedRoles, assignableRoles };
    };

    // Get roles that are included by an assigned role (e.g., Admin includes all other roles)
    const getIncludedRoles = () => {
        const assignedRoles = selectedApiKey?.user_role || [];
        const includedRoles = new Set();

        assignedRoles.forEach(roleName => {
            const roleInfo = roleHierarchy[roleName];
            if (roleInfo && roleInfo.includes) {
                roleInfo.includes.forEach(role => includedRoles.add(role));
            }
        });

        return includedRoles;
    };

    const { assignedRoles, assignableRoles } = splitRoles();
    const includedRoles = getIncludedRoles();

    return (
        <div className="api-key-list-wrapper">
            <div className="api-key-header">
                <h2>Users & API Keys Management</h2>
                <button
                    className="btn btn-primary btn-lg"
                    onClick={handleCreateUserClick}
                >
                    + Create New User
                </button>
            </div>

            {message.text && (
                <Alert
                    variant={message.type === 'error' ? 'danger' : message.type === 'success' ? 'success' : 'info'}
                    dismissible
                    onClose={() => setMessage({})}
                    className="mb-3"
                >
                    {message.text}
                </Alert>
            )}

            <div className="api-keys-table-wrapper">
                <table className="api-keys-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>API Key</th>
                            <th>Expiry</th>
                            <th>Status</th>
                            <th>Roles</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apiKeys.map((apiKey) => (
                            <tr key={apiKey.id} className={apiKey.expired ? 'expired-row' : ''}>
                                <td className="username-cell">
                                    <strong>{apiKey.username}</strong>
                                </td>
                                <td>{apiKey.email}</td>
                                <td className="api-key-cell">
                                    <div className="api-key-display">
                                        <span className="key-text">
                                            {visibility[apiKey.id] ? apiKey.api_key : '•••••••••••••••'}
                                        </span>
                                        <div className="key-actions">
                                            <button
                                                className="btn-icon-small"
                                                onClick={() => toggleVisibility(apiKey.id)}
                                                title={visibility[apiKey.id] ? 'Hide' : 'Show'}
                                            >
                                                {visibility[apiKey.id] ? '👁️' : '👁️‍🗨️'}
                                            </button>
                                            <button
                                                className="btn-icon-small"
                                                onClick={() => handleCopy(apiKey.api_key)}
                                                title="Copy"
                                            >
                                                📋
                                            </button>
                                        </div>
                                    </div>
                                </td>
                                <td className="expiry-cell">{apiKey.expiry}</td>
                                <td className="status-cell">
                                    <span className={`status-badge ${apiKey.expired ? 'expired' : 'active'}`}>
                                        {apiKey.expired ? '❌ Expired' : '✅ Active'}
                                    </span>
                                </td>
                                <td className="roles-cell">
                                    <div className="roles-display">
                                        {apiKey.user_role?.length > 0 ? (
                                            <>
                                                <div className="roles-badges">
                                                    {apiKey.user_role.slice(0, 2).map((role, index) => (
                                                        <span key={index} className="role-badge">
                                                            {role}
                                                        </span>
                                                    ))}
                                                    {apiKey.user_role.length > 2 && (
                                                        <span className="role-badge-more">
                                                            +{apiKey.user_role.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <span className="no-roles">No roles</span>
                                        )}
                                    </div>
                                </td>
                                <td className="actions-cell">
                                    <button
                                        className="btn-action btn-manage"
                                        onClick={() => handleOpenRoleSidebar(apiKey)}
                                        title="Manage Roles"
                                    >
                                        🔐 Manage
                                    </button>
                                    <button
                                        className="btn-action btn-delete"
                                        onClick={() => handleDelete(apiKey.api_key)}
                                        title="Delete User"
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Right Sidebar for Role Management */}
            <Offcanvas
                show={showRoleSidebar}
                onHide={() => setShowRoleSidebar(false)}
                placement="end"
                className="role-sidebar"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        Manage Roles - {selectedApiKey?.username}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="roles-management-container">
                        {/* Assigned Roles */}
                        <div className="roles-section">
                            <h5 className="section-title">Assigned Roles</h5>
                            {assignedRoles.length > 0 ? (
                                <div className="roles-list">
                                    {assignedRoles.map((role) => (
                                        <div key={role} className="role-item assigned">
                                            <div className="role-info">
                                                <span className="role-name">{roleHierarchy[role]?.name || role}</span>
                                                <span className="role-description">{roleHierarchy[role]?.description}</span>
                                                {roleHierarchy[role]?.includes && (
                                                    <div className="role-includes">
                                                        <span className="includes-label">Includes {roleHierarchy[role].includes.length} roles:</span>
                                                        <div className="included-roles">
                                                            {roleHierarchy[role].includes.slice(0, 3).map(includedRole => (
                                                                <span key={includedRole} className="included-role-badge">{includedRole}</span>
                                                            ))}
                                                            {roleHierarchy[role].includes.length > 3 && (
                                                                <span className="included-role-badge">+{roleHierarchy[role].includes.length - 3}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="btn-remove"
                                                onClick={() => handleRoleChange('unassign', role)}
                                                disabled={loading}
                                                title="Remove this role"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-roles-text">No roles assigned yet</p>
                            )}
                        </div>

                        {includedRoles.size > 0 && (
                            <>
                                <div className="divider"></div>
                                <div className="roles-section">
                                    <h5 className="section-title">Roles Included by Assignment</h5>
                                    <p className="section-description">These roles are automatically included with your assigned roles</p>
                                    <div className="roles-list">
                                        {Array.from(includedRoles).map((role) => (
                                            <div key={role} className="role-item included">
                                                <div className="role-info">
                                                    <span className="role-name">{roleHierarchy[role]?.name || role}</span>
                                                    <span className="role-description">{roleHierarchy[role]?.description}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="divider"></div>

                        {/* Available Roles to Assign */}
                        <div className="roles-section">
                            <h5 className="section-title">Available Roles to Assign</h5>
                            {assignableRoles.length > 0 ? (
                                <div className="roles-list">
                                    {assignableRoles.map((role) => (
                                        <div key={role} className="role-item available">
                                            <div className="role-info">
                                                <span className="role-name">{roleHierarchy[role]?.name || role}</span>
                                                <span className="role-description">{roleHierarchy[role]?.description}</span>
                                            </div>
                                            <button
                                                className="btn-add"
                                                onClick={() => handleRoleChange('assign', role)}
                                                disabled={loading}
                                                title="Add this role"
                                            >
                                                +
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-roles-text">All available roles are assigned</p>
                            )}
                        </div>

                        {loading && (
                            <div className="loading-indicator">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Updating roles...
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Right Sidebar for Create User */}
            <Offcanvas
                show={showCreateUserSidebar}
                onHide={() => setShowCreateUserSidebar(false)}
                placement="end"
                className="create-user-sidebar"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Create New User</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form onSubmit={handleCreateUserSubmit}>
                        <Form.Group className="mb-4">
                            <Form.Label className="form-label">Username</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={createFormData.username}
                                onChange={(e) => setCreateFormData({
                                    ...createFormData,
                                    username: e.target.value
                                })}
                                required
                                disabled={creatingUser}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="form-label">Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email address"
                                value={createFormData.email}
                                onChange={(e) => setCreateFormData({
                                    ...createFormData,
                                    email: e.target.value
                                })}
                                required
                                disabled={creatingUser}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="form-label">API Key Expiry Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={createFormData.expiry}
                                onChange={(e) => setCreateFormData({
                                    ...createFormData,
                                    expiry: e.target.value
                                })}
                                required
                                disabled={creatingUser}
                            />
                        </Form.Group>

                        <div className="info-box">
                            <p className="info-text">
                                ℹ️ The user will be created in Keycloak and can be assigned roles after creation.
                            </p>
                        </div>

                        <Button
                            variant="primary"
                            type="submit"
                            className="btn-create-submit"
                            disabled={creatingUser}
                        >
                            {creatingUser ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Creating...
                                </>
                            ) : (
                                'Create User'
                            )}
                        </Button>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default ApiKeyList;
